/**
 * IncomePage — Total income view across all portfolio sources.
 * Route: /app/income
 *
 * Shows: annual total, monthly average, breakdown by category,
 * monthly bar chart, top income-producing holdings table.
 */
import { useEffect } from 'react'
import {
  Banknote, TrendingUp, BarChart2, Coins, Award, Zap, PiggyBank, type LucideProps,
} from 'lucide-react'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'

type LucideIcon = ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import { useIncome } from '@/hooks/useIncome'
import { clsx } from 'clsx'
import type { IncomeSource } from '@/lib/types'
import IncomeTabs from '@/components/ui/IncomeTabs'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtGBP(n: number | null | undefined, decimals = 0): string {
  if (n == null) return 'N/A'
  return `£${n.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`
}

// Category config — colour + icon
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CATEGORY_CONFIG: Record<string, { label: string; color: string; barColor: string; icon: any }> = {
  dividends:  { label: 'Dividends',     color: 'text-emerald',   barColor: '#10b981', icon: Banknote },
  interest:   { label: 'Interest',      color: 'text-blue-light', barColor: '#60a5fa', icon: TrendingUp },
  staking:    { label: 'Staking',       color: 'text-amber-400', barColor: '#fbbf24', icon: Zap },
  prizes:     { label: 'Prizes',        color: 'text-purple-400', barColor: '#a78bfa', icon: Award },
  other:      { label: 'Other',         color: 'text-text-muted', barColor: '#6b7280', icon: Coins },
}

function categoryConfig(cat: string) {
  return CATEGORY_CONFIG[cat] ?? CATEGORY_CONFIG['other']
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse bg-surface-card rounded', className)} />
}

// ── Summary KPI Cards ─────────────────────────────────────────────────────────

function KpiCards() {
  const { summary, loading } = useIncome()

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
      </div>
    )
  }

  if (!summary) return null

  const dividendsAnnual = summary.by_category['dividends'] ?? 0
  const interestAnnual  = summary.by_category['interest']  ?? 0
  const stakingAnnual   = summary.by_category['staking']   ?? 0
  const prizesAnnual    = summary.by_category['prizes']    ?? 0

  const cards = [
    { label: 'Dividends',           value: fmtGBP(dividendsAnnual),      sub: fmtPct(summary.total_annual ? dividendsAnnual / summary.total_annual * 100 : 0) + ' of total', icon: Banknote, color: 'text-emerald' },
    { label: 'Interest & Dist.',    value: fmtGBP(interestAnnual),        sub: fmtPct(summary.total_annual ? interestAnnual / summary.total_annual * 100 : 0) + ' of total', icon: TrendingUp, color: 'text-blue-light' },
    { label: 'Prizes & Staking',    value: fmtGBP(prizesAnnual + stakingAnnual), sub: fmtPct(summary.total_annual ? (prizesAnnual + stakingAnnual) / summary.total_annual * 100 : 0) + ' of total', icon: Award, color: 'text-amber-400' },
  ]

  return (
    <div className="space-y-4">
      {/* The page's one number — income per year across everything */}
      <div className="card p-5">
        <p className="panel-title flex items-center gap-2">
          <PiggyBank size={14} className="text-emerald" aria-hidden />
          Total income per year
        </p>
        <p className="stat-hero text-text-primary mt-1">{fmtGBP(summary.total_annual)}</p>
        <p className="text-xs text-text-muted mt-1">{fmtGBP(summary.total_monthly)} a month on average</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">
        {cards.map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={15} className={color} />
              <span className="text-xs text-text-muted">{label}</span>
            </div>
            <p className={clsx('stat-value font-mono', color)}>{value}</p>
            <p className="text-xs text-text-muted mt-1">{sub}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Monthly Bar Chart ─────────────────────────────────────────────────────────

function MonthlyChart() {
  const { summary, loading } = useIncome()

  if (loading) return <Skeleton className="h-52" />
  if (!summary) return null

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const chartData = MONTHS.map(m => ({
    month: m,
    income: summary.monthly_forecast[m] ?? 0,
  }))

  const maxVal = Math.max(...chartData.map(d => d.income))

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-4 flex items-center gap-2">
        <BarChart2 size={15} className="text-blue-light" />
        Monthly Income Forecast
      </h2>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={v => `£${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={44} />
          <Tooltip
            formatter={(v: number) => [fmtGBP(v), 'Income']}
            contentStyle={{ background: '#1e2030', border: '1px solid #2d3152', borderRadius: 6, fontSize: 12, color: 'rgb(var(--text-primary))'}}
            labelStyle={{ color: '#e2e8f0' }}
          />
          <Bar dataKey="income" radius={[3, 3, 0, 0]}>
            {chartData.map((entry, idx) => (
              <Cell
                key={idx}
                fill={entry.income === maxVal ? '#10b981' : '#3b82f6'}
                opacity={entry.income === maxVal ? 1 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Category Breakdown ────────────────────────────────────────────────────────

function CategoryBreakdown() {
  const { summary, loading } = useIncome()

  if (loading) return <Skeleton className="h-52" />
  if (!summary) return null

  const total = summary.total_annual
  const entries = Object.entries(summary.by_category).sort(([, a], [, b]) => b - a)

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-4 flex items-center gap-2">
        <Banknote size={15} className="text-emerald" />
        Income by Category
      </h2>
      <div className="space-y-3">
        {entries.map(([cat, amt]) => {
          const cfg = categoryConfig(cat)
          const pct = total > 0 ? (amt / total) * 100 : 0
          const Icon = cfg.icon
          return (
            <div key={cat}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Icon size={13} className={cfg.color} />
                  <span className="text-xs text-text-primary">{cfg.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-text-muted">{fmtPct(pct)}</span>
                  <span className={clsx('text-sm font-mono font-semibold', cfg.color)}>{fmtGBP(amt)}</span>
                </div>
              </div>
              <div className="w-full bg-surface-card rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-500 [width:var(--bar-w)] [background-color:var(--bar-c)]"
                  // eslint-disable-next-line react/forbid-dom-props
                  style={{ '--bar-w': `${pct}%`, '--bar-c': cfg.barColor } as React.CSSProperties}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Top Sources Table ─────────────────────────────────────────────────────────

function SourcesTable() {
  const { summary, loading } = useIncome()

  if (loading) {
    return (
      <div className="card p-4">
        <div className="animate-pulse space-y-2">
          <Skeleton className="h-4 w-1/3 mb-4" />
          {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10" />)}
        </div>
      </div>
    )
  }

  if (!summary?.sources.length) return null

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Coins size={15} className="text-amber-400" />
        All Income Sources
      </h2>
      <div className="overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3 hidden sm:table-cell">Type</th>
              <th className="!text-right py-2 pr-3">Annual</th>
              <th className="!text-right py-2 pr-3 hidden md:table-cell">Monthly</th>
              <th className="py-2 hidden lg:table-cell">Notes</th>
            </tr>
          </thead>
          <tbody>
            {summary.sources.map((s: IncomeSource, idx: number) => {
              const cfg = categoryConfig(s.category)
              const Icon = cfg.icon
              return (
                <tr key={idx}>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <Icon size={13} className={cfg.color} />
                      <span className="text-text-primary font-medium truncate max-w-[160px]">{s.name}</span>
                    </div>
                  </td>
                  <td className="py-2 pr-3 hidden sm:table-cell">
                    <span className={clsx('text-xs px-1.5 py-0.5 rounded font-medium', cfg.color, `bg-surface-card`)}>
                      {cfg.label}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono font-semibold text-text-primary">
                    {fmtGBP(s.annual_income)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-text-muted hidden md:table-cell">
                    {fmtGBP(s.monthly_income)}
                  </td>
                  <td className="py-2 text-xs text-text-muted hidden lg:table-cell truncate max-w-[200px]">
                    {s.notes ?? '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function IncomePage() {
  const { fetch, loading } = useIncome()

  useEffect(() => {
    fetch()
  }, [fetch])

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <PiggyBank size={22} className="text-emerald" />
            Income Summary
          </h1>
          <p className="text-sm text-text-muted mt-0.5">
            Total income across all accounts — dividends, interest, staking & prizes
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetch()}
          disabled={loading}
          className="btn-secondary text-xs"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <IncomeTabs />

      {/* KPI cards */}
      <KpiCards />

      {/* Chart + Category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MonthlyChart />
        <CategoryBreakdown />
      </div>

      {/* Full sources table */}
      <SourcesTable />
    </div>
  )
}
